import type { BillingInterval, BillingPlan, PlanChoice } from 'definitions/billing-plans';
import { useState } from 'react';
import { BILLING_PLANS } from 'definitions';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { readApiError } from './billing/api-error';
import { saveCsv } from './billing/download-csv';

export type AdminCompanyRow = {
  id: number;
  name: string;
  type: string | null;
  parentCompanyId: number | null;
  billingMode: string;
  /** How it is actually billed: its own mode when invoice or free, otherwise its payer's. */
  effectiveBillingMode: string;
  subscriptionStatus: string | null;
  /** The plan it is on; null for an agency (a fee, not a plan) or with no subscription. */
  plan: string | null;
  interval: string | null;
  /** Whether leaving card mode would revoke something. Computed server-side so the confirm cannot get it wrong. */
  hasLiveSubscription: boolean;
  trialEnd: string | null;
  sessionsThisMonth: number;
};

export type AdminCompanies = { companies: AdminCompanyRow[]; currency: string };

/** Polar's discount object, as much of it as this screen reads. */
export type AdminDiscount = {
  id: string;
  name: string;
  code: string | null;
  type: 'percentage' | 'fixed';
  duration: 'once' | 'forever' | 'repeating';
  durationInMonths?: number | null;
  basisPoints?: number | null;
  /** Fixed discounts: minor units of `currency`. */
  amount?: number | null;
  currency?: string | null;
  maxRedemptions: number | null;
  redemptionsCount?: number | null;
  endsAt: string | null;
};

export type CreateDiscountPayload = {
  name: string;
  code?: string;
  type: 'percentage' | 'fixed';
  duration: 'once' | 'forever' | 'repeating';
  /** 1 to 36, required for a repeating discount. */
  durationInMonths?: number;
  /** 1 to 10000 (0.01% to 100%), percentage discounts. */
  basisPoints?: number;
  /** A positive integer in minor units of the server's billing currency, fixed discounts. */
  amount?: number;
  maxRedemptions?: number;
};

export type CreateCompanyPayload = {
  name: string;
  type: 'Client Account' | 'Agency';
  billingMode: 'invoice' | 'free';
  owner: { email: string; firstName: string; lastName: string };
  /** An organization's plan; an agency has a fee and sends none. */
  plan?: BillingPlan;
  interval?: BillingInterval;
};

/** What the create route answers: the companies row as inserted (not an admin list row) and the invited owner. */
type CreateCompanyResult = { company: { id: number; name: string }; owner: { id: number; email: string } };
type ModeChangeResult = AdminCompanyRow & { revokeError: string | null };
type SetModeParams = { companyId: number; billingMode: string };
type ExtendTrialParams = { companyId: number; trialEnd: string };
type ChangePlanParams = { companyId: number } & PlanChoice;

/**
 * Whose plan Admin, Billing may change: an account off the card flow (its plan is a label Polar
 * does not hold), and not an agency, which has a fee instead of a plan.
 */
export const canChangePlan = (company: AdminCompanyRow): boolean =>
  company.effectiveBillingMode !== 'card' && company.type !== 'Agency';

const DEFAULT_PLAN_CHOICE: PlanChoice = { plan: 'unlimited', interval: 'year' };

const isPlan = (value: string | null): value is BillingPlan =>
  value !== null && (BILLING_PLANS as readonly string[]).includes(value);

/**
 * Why a mode change needs a confirm before it is sent:
 * - `revoke`: leaving card while a subscription is live cancels it in Polar, immediately.
 * - `to-card`: moving to card puts serving behind a subscription the company may not have.
 */
export type PendingModeChange = { company: AdminCompanyRow; mode: string; reason: 'revoke' | 'to-card' };

const COMPANIES_KEY = ['ADMIN_BILLING_COMPANIES'];
const DISCOUNTS_KEY = ['ADMIN_DISCOUNTS'];

export const isDiscountEnded = (discount: AdminDiscount, now: number) =>
  !!discount.endsAt && new Date(discount.endsAt).getTime() <= now;

const toastFailure = (title: string) => (error: unknown) =>
  toast.error(title, { description: readApiError(error).message ?? 'Please try again in a moment.' });

/**
 * Superadmin billing tools, as one manager for the Admin > Billing screen:
 * the two lists (React Query), every mutation (each invalidating the list it
 * changes), and the screen's dialog state: the pending mode change, the trial
 * being extended, the discount being disabled.
 */
export const useAdminBilling = () => {
  const client = useClient();
  const queryClient = useQueryClient();

  const companiesQuery = useQuery<AdminCompanies>({
    queryKey: COMPANIES_KEY,
    queryFn: () => client.get('/admin/billing/companies'),
  });

  const discountsQuery = useQuery<AdminDiscount[]>({
    queryKey: DISCOUNTS_KEY,
    queryFn: () => client.get('/admin/billing/discounts'),
  });

  const [pendingModeChange, setPendingModeChange] = useState<PendingModeChange | null>(null);
  const [extendingTrialFor, setExtendingTrialFor] = useState<AdminCompanyRow | null>(null);
  const [trialEndDate, setTrialEndDate] = useState('');
  const [disablingDiscount, setDisablingDiscount] = useState<AdminDiscount | null>(null);
  const [changingPlanFor, setChangingPlanFor] = useState<AdminCompanyRow | null>(null);
  const [planChoice, setPlanChoice] = useState<PlanChoice>(DEFAULT_PLAN_CHOICE);

  const setModeMutation = useMutation({
    mutationFn: ({ companyId, billingMode }: SetModeParams) =>
      client.patch<ModeChangeResult>(`/admin/billing/companies/${companyId}/mode`, { billingMode }),
    onSuccess: (result: ModeChangeResult) => {
      // The mode change stands even when the revoke failed; the card may still be charged.
      if (result.revokeError) {
        toast.warning('Billing mode changed, but the subscription is still live', {
          description: `Revoke it in Polar. ${result.revokeError}`,
          duration: 15_000,
        });
      } else {
        toast.success('Billing mode changed.');
      }
    },
    onError: toastFailure('Could not change the billing mode'),
    onSettled: () => queryClient.invalidateQueries({ queryKey: COMPANIES_KEY }),
  });

  const extendTrialMutation = useMutation({
    mutationFn: ({ companyId, trialEnd }: ExtendTrialParams) =>
      client.post(`/admin/billing/companies/${companyId}/extend-trial`, { trialEnd }),
    onSuccess: () => {
      // Our mirror updates when Polar's webhook lands, so the date may lag a moment.
      toast.success('Trial extended. The new date shows once Polar confirms it.');
      queryClient.invalidateQueries({ queryKey: COMPANIES_KEY });
    },
    onError: toastFailure('Could not extend the trial'),
  });

  const changePlanMutation = useMutation({
    mutationFn: ({ companyId, plan, interval }: ChangePlanParams) =>
      client.patch(`/admin/billing/companies/${companyId}/plan`, { plan, interval }),
    onSuccess: () => {
      toast.success('Plan changed.');
      queryClient.invalidateQueries({ queryKey: COMPANIES_KEY });
    },
    onError: toastFailure('Could not change the plan'),
  });

  const createCompanyMutation = useMutation({
    mutationFn: (payload: CreateCompanyPayload) =>
      client.post<CreateCompanyResult>('/admin/billing/companies', payload),
    onSuccess: (result: CreateCompanyResult) => {
      toast.success(`${result.company.name} created.`, {
        description: `An invitation was sent to ${result.owner.email}.`,
      });
      queryClient.invalidateQueries({ queryKey: COMPANIES_KEY });
    },
    onError: toastFailure('Could not create the company'),
  });

  const createDiscountMutation = useMutation({
    mutationFn: (payload: CreateDiscountPayload) => client.post<AdminDiscount>('/admin/billing/discounts', payload),
    onSuccess: () => {
      toast.success('Discount created.');
      queryClient.invalidateQueries({ queryKey: DISCOUNTS_KEY });
    },
    onError: toastFailure('Could not create the discount'),
  });

  const disableDiscountMutation = useMutation({
    mutationFn: (discountId: string) =>
      client.patch<AdminDiscount>(`/admin/billing/discounts/${discountId}/disable`, {}),
    onSuccess: () => {
      toast.success('Discount disabled.');
      queryClient.invalidateQueries({ queryKey: DISCOUNTS_KEY });
    },
    onError: toastFailure('Could not disable the discount'),
  });

  const applyMode = async (company: AdminCompanyRow, mode: string) => {
    await setModeMutation.mutateAsync({ companyId: company.id, billingMode: mode }).catch(() => undefined);
  };

  const requestModeChange = (company: AdminCompanyRow, mode: string) => {
    if (mode === company.billingMode) return;
    if (company.billingMode === 'card' && company.hasLiveSubscription) {
      setPendingModeChange({ company, mode, reason: 'revoke' });
      return;
    }
    if (mode === 'card') {
      setPendingModeChange({ company, mode, reason: 'to-card' });
      return;
    }
    applyMode(company, mode);
  };

  const confirmModeChange = async () => {
    if (!pendingModeChange) return;
    await applyMode(pendingModeChange.company, pendingModeChange.mode);
    setPendingModeChange(null);
  };

  const openExtendTrial = (company: AdminCompanyRow) => {
    setTrialEndDate('');
    setExtendingTrialFor(company);
  };

  const closeExtendTrial = () => {
    setTrialEndDate('');
    setExtendingTrialFor(null);
  };

  // A past date is not an extension: Polar reads it as "the trial is over, charge now".
  const trialEndIso = trialEndDate ? new Date(`${trialEndDate}T23:59:59`).toISOString() : null;
  const trialEndBlockingReason = !trialEndDate
    ? 'Choose the new trial end date'
    : trialEndIso && new Date(trialEndIso).getTime() <= Date.now()
      ? 'Choose a date in the future'
      : undefined;

  const confirmExtendTrial = async () => {
    if (!extendingTrialFor || !trialEndIso || trialEndBlockingReason) return;
    try {
      await extendTrialMutation.mutateAsync({ companyId: extendingTrialFor.id, trialEnd: trialEndIso });
      closeExtendTrial();
    } catch {
      // Toasted by the mutation; the dialog stays open with the date still in it.
    }
  };

  const openChangePlan = (company: AdminCompanyRow) => {
    setPlanChoice({
      plan: isPlan(company.plan) ? company.plan : DEFAULT_PLAN_CHOICE.plan,
      interval: company.interval === 'month' ? 'month' : 'year',
    });
    setChangingPlanFor(company);
  };

  const closeChangePlan = () => setChangingPlanFor(null);

  const planChangeBlockingReason =
    changingPlanFor && changingPlanFor.plan === planChoice.plan && changingPlanFor.interval === planChoice.interval
      ? 'Choose a different plan or interval'
      : undefined;

  const confirmChangePlan = async () => {
    if (!changingPlanFor || planChangeBlockingReason) return;
    try {
      await changePlanMutation.mutateAsync({ companyId: changingPlanFor.id, ...planChoice });
      closeChangePlan();
    } catch {
      // Toasted by the mutation; the dialog stays open with the choice still in it.
    }
  };

  const confirmDisableDiscount = async () => {
    if (!disablingDiscount) return;
    try {
      await disableDiscountMutation.mutateAsync(disablingDiscount.id);
      setDisablingDiscount(null);
    } catch {
      // Toasted by the mutation.
    }
  };

  /**
   * The report sits behind Bearer auth like everything else, so it is fetched
   * as text and saved through saveCsv rather than linked.
   */
  const downloadUsageReport = async (company: AdminCompanyRow) => {
    try {
      const csv = await client.getText(`/admin/billing/companies/${company.id}/usage-report`);
      saveCsv({ csv, filename: `usage-${company.name.replace(/[^a-z0-9]+/gi, '-')}-current.csv` });
    } catch (error) {
      toastFailure('Could not download the usage report')(error);
    }
  };

  const companiesData = companiesQuery.data;

  return {
    companies: {
      data: companiesData,
      isLoading: !companiesData && !companiesQuery.isError,
      isError: companiesQuery.isError,
      retry: () => companiesQuery.refetch(),
    },
    discounts: {
      data: discountsQuery.data,
      isLoading: !discountsQuery.data && !discountsQuery.isError,
      isError: discountsQuery.isError,
      retry: () => discountsQuery.refetch(),
    },
    /** For labelling fixed discount amounts: the server's billing currency. */
    currency: companiesData?.currency ?? null,
    isChangingMode: setModeMutation.isPending,
    pendingModeChange,
    requestModeChange,
    confirmModeChange,
    cancelModeChange: () => setPendingModeChange(null),
    extendingTrialFor,
    trialEndDate,
    setTrialEndDate,
    trialEndBlockingReason,
    openExtendTrial,
    closeExtendTrial,
    confirmExtendTrial,
    disablingDiscount,
    requestDisableDiscount: setDisablingDiscount,
    cancelDisableDiscount: () => setDisablingDiscount(null),
    confirmDisableDiscount,
    createDiscount: (payload: CreateDiscountPayload) => createDiscountMutation.mutateAsync(payload),
    createCompany: (payload: CreateCompanyPayload) => createCompanyMutation.mutateAsync(payload),
    changingPlanFor,
    planChoice,
    setPlanChoice,
    planChangeBlockingReason,
    openChangePlan,
    closeChangePlan,
    confirmChangePlan,
    downloadUsageReport,
  };
};

export type AdminBillingManager = ReturnType<typeof useAdminBilling>;
